import { Box, Chip, Divider, Stack, Typography, useTheme } from "@mui/material";

import { PrimaryButton, SecondaryButton } from "@/components/common";

import { MarketplaceLineItemRow } from "./MarketplaceLineItemRow";
import { formatWooPrice } from "../utils/addressHelpers";
import { marketplaceTypography as mt } from "../constants/marketplaceTypography";
import { marketplaceCardSx } from "../constants/marketplaceCardStyles";
import type { WooOrder } from "../types";
import {
  formatOrderDate,
  getOrderStatusColor,
  getOrderStatusLabel,
} from "../utils/orderHelpers";

export interface MarketplaceOrderCardProps {
  order: WooOrder;
  showPendingActions?: boolean;
  disabled?: boolean;
  cancelling?: boolean;
  monitoringOrderId?: number | null;
  onPay?: () => void;
  onCancel?: () => void;
}

/** Card de pedido — Meus pedidos (estilo Amazon). */
export function MarketplaceOrderCard({
  order,
  showPendingActions = false,
  disabled = false,
  cancelling = false,
  monitoringOrderId = null,
  onPay,
  onCancel,
}: MarketplaceOrderCardProps) {
  const theme = useTheme();
  const isMonitoring = monitoringOrderId === order.id;
  const actionsDisabled =
    disabled || cancelling || Boolean(monitoringOrderId && !isMonitoring);

  return (
    <Box sx={{ ...marketplaceCardSx(theme), p: 1.5 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
        sx={{ mb: 1.25 }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography sx={{ ...mt.body, fontWeight: 700 }}>
              Pedido #{order.id}
            </Typography>
            <Chip
              label={getOrderStatusLabel(order.status)}
              size="small"
              color={getOrderStatusColor(order.status)}
              sx={{ height: 22 }}
            />
          </Stack>
          <Typography sx={{ ...mt.bodySecondary, color: "text.secondary", mt: 0.25 }}>
            {formatOrderDate(order.dateCreated)} · {order.storeName} ·{" "}
            {formatWooPrice(order.total, order.currency)}
          </Typography>
        </Box>
        {showPendingActions && (onPay || onCancel) ? (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            {onPay ? (
              <PrimaryButton
                width={{ xs: "100%", sm: "fit-content" }}
                height="44px"
                loading={isMonitoring}
                disabled={actionsDisabled}
                onClick={onPay}
              >
                Pagar agora
              </PrimaryButton>
            ) : null}
            {onCancel ? (
              <SecondaryButton
                width={{ xs: "100%", sm: "fit-content" }}
                height="44px"
                disabled={actionsDisabled || isMonitoring}
                onClick={onCancel}
              >
                {cancelling ? "Cancelando..." : "Cancelar pedido"}
              </SecondaryButton>
            ) : null}
          </Stack>
        ) : null}
      </Stack>

      <Divider sx={{ mb: 1.25, opacity: 0.6 }} />

      <Stack spacing={1.25}>
        {order.lineItems.map((line) => (
          <MarketplaceLineItemRow
            key={line.id}
            name={line.name}
            price={line.price}
            quantity={line.quantity}
            imageSrc={line.imageSrc}
            currencyCode={order.currency}
          />
        ))}
      </Stack>
    </Box>
  );
}
